import { Cell } from '@pelckmans/elementis/components/table';
import { useTranslation } from 'react-i18next';
import { splitAssignmentsByEndDateCategory, splitAssignmentsByPastEndDateCategory } from '../../utils';
import { TeacherAssignment } from '../api/AssignmentTeacherApi';
import AssignmentDivision from './AssignmentDivision';

const BASE = 'PAGES.ASSIGNMENTS.TEACHER.SECTION';

function AssignmentsBody({ assignments }: { assignments: TeacherAssignment[] }) {
  const { assignmentsEndIn24hours, assignmentsEndIn7Days, assignmentsEndOver7Days } =
    splitAssignmentsByEndDateCategory(assignments);

  return (
    <>
      <AssignmentDivision endDateCategory="TODAY" assignments={assignmentsEndIn24hours} />
      <AssignmentDivision endDateCategory="THIS_WEEK" assignments={assignmentsEndIn7Days} />
      <AssignmentDivision endDateCategory="LATER" assignments={assignmentsEndOver7Days} />
    </>
  );
}

function ArchivedAssignmentsBody({ assignments }: { assignments: TeacherAssignment[] }) {
  const {
    i18n: { language },
  } = useTranslation();

  const { endedThisWeek, endedLastWeek, endedByMonth } = splitAssignmentsByPastEndDateCategory(assignments, language);

  return (
    <>
      <AssignmentDivision endDateCategory="LAST_WEEK" assignments={endedThisWeek} />
      <AssignmentDivision endDateCategory="LAST_TWO_WEEKS" assignments={endedLastWeek} />
      {Object.entries(endedByMonth).map(([key, value]) => (
        <AssignmentDivision key={key} endDateCategory="IN_LAST_MONTH" assignments={value} monthAndYear={key} />
      ))}
    </>
  );
}

type AssignmentsTableProps = {
  assignments: TeacherAssignment[];
  archived?: boolean;
  id: string;
};

export default function AssignmentsTable({ assignments, archived, id }: AssignmentsTableProps) {
  const { t } = useTranslation();

  return (
    <div className="scroll-table-container">
      <table className="assignments-table" data-testid="assignments-table" aria-labelledby={id}>
        <thead>
          <tr>
            <Cell.HeaderText title={t(`${BASE}.TABLE.HEADER.MATERIAL`)} sticky minWidth="xl" maxWidth="xl" />
            <Cell.HeaderText title={t(`${BASE}.TABLE.HEADER.SECTION`)} sticky minWidth="md" maxWidth="md" />
            <Cell.HeaderText title={t(`${BASE}.TABLE.HEADER.PERIOD`)} sticky minWidth="sm" maxWidth="sm" />
            <Cell.HeaderText title={t(`${BASE}.TABLE.HEADER.GROUP`)} sticky minWidth="md" maxWidth="md" />
            <Cell.HeaderText title={t(`${BASE}.TABLE.HEADER.STUDENTS`)} sticky minWidth="xs" />
            <Cell.HeaderEmpty className="actions-cell" sticky />
          </tr>
        </thead>
        <tbody>
          {archived ? (
            <ArchivedAssignmentsBody assignments={assignments} />
          ) : (
            <AssignmentsBody assignments={assignments} />
          )}
        </tbody>
      </table>
    </div>
  );
}
